import { useCallback, useEffect } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import { skuCreateFormSchema, type SKUQuerySchema } from '@/_pages/settings/inventory-kits/shared/schemas/skus';
import { dateTime, useBoolean } from '@/_shared';

import { defaultValues } from './shared';

export type Props = {
  onChange: (v: SKUQuerySchema) => number;
};

export function useCreateSKU({ onChange }: Props) {
  const [isPending, setIsPending] = useBoolean();
  const form = useForm({
    resolver: zodResolver(skuCreateFormSchema),
    defaultValues: { ...defaultValues, id: dateTime.now().valueOf() },
  });
  const sku = form.watch('sku');
  // const { isPending, mutateAsync } = useCreateMutation();

  const validateForm = useCallback(async () => {
    const newV = form.getValues();
    const res = await form.trigger();
    if (!res) {
      // Return undefined to differentiate between API error and form validation error
      return undefined;
    }

    return newV;
  }, [form]);

  const onSubmit = useCallback(async () => {
    setIsPending.setTrue();
    const newV = await validateForm();
    if (newV) {
      const res = onChange({ ...newV });
      if (res === -2) {
        form.setError('sku', { type: 'manual', message: 'SKU already exists.' });
        setIsPending.setFalse();
        return false;
      }
    }
    setIsPending.setFalse();
    form.reset();
    form.setValue('id', dateTime.now().valueOf());
    return Boolean(newV);
  }, [form, onChange, setIsPending, validateForm]);

  useEffect(() => {
    if (sku && form.formState.errors.sku?.type === 'manual') {
      form.clearErrors();
    }
  }, [sku, form]);

  return { id: form.getValues('id'), form, onSubmit, validateForm, isPending };
}

export type UseCreateSKU = ReturnType<typeof useCreateSKU>;
